import { cn } from "@/infrastructure/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/shared/components/ui/avatar";
import { Button } from "@/shared/components/ui/button";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Bot, Copy, Check, Share, AlertCircle } from "lucide-react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { MessageAttachments } from "./ChatAttachments";
import type { ChatAttachment } from "../types/chatBase";

interface ChatMessageProps {
  /** Arquivos anexados pelo botão + (AB#87121), só nas mensagens do usuário. */
  attachments?: ChatAttachment[];
  /** Abre um anexo da mensagem (link temporário). */
  onOpenAttachment?: (attachment: ChatAttachment) => void;
  message: {
    id: string;
    message: {
      content: string;
      type?: "human" | "ai";
    };
    type?: "human" | "ai";
    created_at: string;
    agent_id?: string;
    user_id?: string;
  };
  author?: {
    id: string;
    name?: string;
    logo_url?: string;
    avatar_url?: string;
    email?: string;
    user_metadata?: {
      avatar_url?: string;
      full_name?: string;
    };
  };
  isAgent: boolean;
  /** Render the assistant message as an inline error (destructive styling).
   * Used when the chat flow itself failed (watchdog timeout, SSE error,
   * POST rejected) so the user sees the failure anchored to the turn it
   * relates to, instead of as a banner at the top of the page. */
  isError?: boolean;
}

/** Shared prose classes for chat markdown — @tailwindcss/typography handles the heavy lifting */
const userProseClasses = cn(
  "prose prose-sm max-w-none break-words text-foreground",
  "prose-neutral dark:prose-invert",
  "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
);

const aiProseClasses = cn(
  "prose prose-sm max-w-none break-words text-foreground/70",
  "prose-neutral dark:prose-invert",
  "[&>*:first-child]:mt-0 [&>*:last-child]:mb-0",
  // Softer headings and bold
  "prose-headings:text-foreground/80 prose-headings:font-semibold",
  "prose-strong:text-foreground/80 prose-strong:font-semibold",
  // Tighter spacing for chat context
  "prose-p:my-2 prose-li:my-1",
  // Code blocks styling
  "prose-pre:bg-muted prose-pre:rounded-lg",
  "prose-code:before:content-none prose-code:after:content-none",
  // Links
  "prose-a:text-primary/80 prose-a:no-underline hover:prose-a:underline",
  // Tables
  "prose-thead:bg-muted/50",
  "prose-th:border prose-th:border-border prose-th:px-3 prose-th:py-2 prose-th:text-foreground/70",
  "prose-td:border prose-td:border-border prose-td:px-3 prose-td:py-2",
);

export default function ChatMessage({ message, author, isAgent, isError = false, attachments, onOpenAttachment }: ChatMessageProps) {
  const { t } = useTranslation();
  const [isCopied, setIsCopied] = useState(false);

  const messageData = typeof message.message === "string"
    ? JSON.parse(message.message)
    : message.message;

  const authorName = isAgent
    ? author?.name
    : author?.user_metadata?.full_name || author?.email;

  const authorAvatar = isAgent
    ? author?.logo_url
    : author?.user_metadata?.avatar_url;

  const authorInitial = authorName?.[0]?.toUpperCase();

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(messageData.content);
      setIsCopied(true);
      setTimeout(() => setIsCopied(false), 2000);
    } catch (error) {
      console.error('Erro ao copiar:', error);
    }
  };

  const handleShare = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: t('playground.message.aiResponseTitle', { name: authorName }),
          text: messageData.content,
        });
      } else {
        await navigator.clipboard.writeText(messageData.content);
      }
    } catch (error) {
      console.error('Erro ao compartilhar:', error);
    }
  };

  // User messages: right-aligned with visible bubble
  if (!isAgent) {
    return (
      <div className="w-full py-1 group">
        <div className="flex justify-end px-4">
          <div className="max-w-[75%] space-y-0.5">
            <div className="rounded-2xl rounded-br-sm bg-muted/50 dark:bg-muted/40 px-4 py-2.5">
              <MessageAttachments attachments={attachments} onOpen={onOpenAttachment} />
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                className={userProseClasses}
              >
                {messageData.content}
              </ReactMarkdown>
            </div>
            <div className="flex justify-end pr-1">
              <span className="text-[10px] text-muted-foreground/40">
                {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // AI messages: left-aligned, clean, with avatar
  return (
    <div className="w-full py-1 group">
      <div className="flex gap-3 px-4 max-w-none">
        {/* Avatar — destructive accent when this turn errored */}
        <Avatar
          className={cn(
            "h-9 w-9 border shadow-sm shrink-0 mt-0.5",
            isError ? "border-destructive/30" : "border-border/20",
          )}
        >
          {!isError && authorAvatar ? (
            <AvatarImage
              src={authorAvatar}
              alt={authorName || ""}
              className="object-cover"
            />
          ) : (
            <AvatarFallback
              className={cn(
                "text-sm font-medium",
                isError
                  ? "bg-destructive/10 text-destructive border-destructive/20"
                  : "bg-primary/10 text-primary border-primary/20",
              )}
            >
              {isError ? <AlertCircle className="h-4 w-4" /> : (authorInitial || <Bot className="h-4 w-4" />)}
            </AvatarFallback>
          )}
        </Avatar>

        {/* Content */}
        <div className="flex-1 min-w-0 space-y-0.5">
          {/* Author + timestamp */}
          <div className="flex items-center gap-2">
            <span
              className={cn(
                "text-[13px] font-semibold",
                isError ? "text-destructive" : "text-foreground/80",
              )}
            >
              {isError ? t('playground.errors.title', 'Erro na interação') : authorName}
            </span>
            <span className="text-[11px] text-muted-foreground/50">
              {new Date(message.created_at).toLocaleTimeString('pt-BR', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </span>
          </div>

          {/* Message content */}
          <div className="relative">
            {isError ? (
              // Destructive-styled box for error rows. No markdown — plain
              // text so we never accidentally render a stack trace or
              // server-side detail as formatted output.
              <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive/90 break-words whitespace-pre-wrap">
                {messageData.content}
              </div>
            ) : (
              <ReactMarkdown
                remarkPlugins={[remarkGfm]}
                className={aiProseClasses}
              >
                {messageData.content}
              </ReactMarkdown>
            )}

            {/* Action buttons — visible on hover only. Hidden on error rows
                since copy/share on an error message isn't useful. */}
            {!isError && (
              <div className="flex justify-end gap-0.5 mt-1 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleShare}
                  className="h-6 w-6 p-0 text-muted-foreground/40 hover:text-foreground hover:bg-muted/50"
                  title={t('playground.message.shareTitle')}
                >
                  <Share className="h-3 w-3" />
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={handleCopy}
                  className={cn(
                    "h-6 w-6 p-0 text-muted-foreground/40 hover:text-foreground hover:bg-muted/50",
                    isCopied && "text-green-600 dark:text-green-400 opacity-100"
                  )}
                  title={t('playground.message.copyTitle')}
                >
                  {isCopied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
